/**
 * API Configuration
 * Base URL and authentication token management
 */

const API_CONFIG = {
  baseUrl: process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000',
  endpoints: {
    // Authentication
    sendVerificationOTPs: '/api/user/send-verification-otps',
    verifyOTP: '/api/user/verify-otp',
    register: '/api/user/register',
    login: '/api/user/login',
    
    // Profile
    profile: '/api/user/profile',
    changePassword: '/api/user/change-password',
    
    // Referrals
    referrals: '/api/user/referrals',
    
    // Wallet
    wallet: '/api/user/wallet',
    walletTransactions: '/api/user/wallet/transactions',
    
    // Trades
    trades: '/api/user/trades',
    tradeById: (tradeId) => `/api/user/trades/${tradeId}`,
    forexTrades: '/api/user/trades/forex',
    cryptoTrades: '/api/user/trades/crypto',
    goldTrades: '/api/user/trades/gold',
    activeTrades: '/api/user/trades/active',
    tradeHistory: '/api/user/trades/history',
    tradeStats: '/api/user/trades/stats',
    
    // Market Data
    symbols: '/api/user/symbols',
    symbolPrice: (symbol) => `/api/user/symbols/${symbol}/price`,
    
    // Dashboard
    dashboard: '/api/user/dashboard',
    
    // KYC
    kyc: '/api/user/kyc',
    kycDetails: '/api/user/kyc/details',
    kycDocument: (documentType) => `/api/user/kyc/document/${documentType}`,
    
    // Payments
    paymentMethods: '/api/user/payment-methods',
    deposit: '/api/user/deposit',
    withdraw: '/api/user/withdraw',
    transactions: '/api/user/transactions',
    
    // Bank Accounts
    bankAccounts: '/api/user/bank-accounts',
    bankAccountById: (accountId) => `/api/user/bank-accounts/${accountId}`,
    
    // Popups
    popups: '/api/user/popups',
    popupViewed: (popupId) => `/api/user/popups/${popupId}/viewed`,
  }
};

/**
 * Get authentication token from localStorage
 */
export const getAuthToken = () => {
  return localStorage.getItem('authToken') || '';
};

/**
 * Set authentication token in localStorage
 */
export const setAuthToken = (token) => {
  localStorage.setItem('authToken', token);
};

/**
 * Remove authentication token from localStorage
 */
export const removeAuthToken = () => {
  localStorage.removeItem('authToken');
};

/**
 * Get base URL
 */
export const getBaseUrl = () => {
  return API_CONFIG.baseUrl;
};

/**
 * Build image URL from a path
 * Handles both full URLs and relative paths
 * @param {string} imagePath - Image path (can be full URL or relative path)
 * @returns {string} Full image URL
 */
export const buildImageUrl = (imagePath) => {
  if (!imagePath) return '';
  
  // If already a full URL, use it as is
  if (imagePath.startsWith('http://') || imagePath.startsWith('https://')) {
    return imagePath;
  }
  
  // Normalize path separators (handle Windows backslashes)
  const normalizedPath = imagePath.replace(/\\/g, '/');
  
  // Get base URL
  const baseUrl = API_CONFIG.baseUrl || process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  // Ensure path starts with /
  const cleanPath = normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
  
  return `${cleanBaseUrl}${cleanPath}`;
};

/**
 * Build full URL for an endpoint
 * Ensures the URL is always absolute (starts with http:// or https://)
 */
export const buildUrl = (endpoint) => {
  let baseUrl = API_CONFIG.baseUrl;
  
  // Ensure baseUrl is absolute (starts with http:// or https://)
  // If not, default to localhost
  if (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://')) {
    baseUrl = 'http://localhost:3000';
  }
  
  // Ensure endpoint starts with /
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  
  // Remove trailing slash from baseUrl if present
  const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  
  return `${cleanBaseUrl}${cleanEndpoint}`;
};

/**
 * Get headers for authenticated requests
 */
export const getAuthHeaders = (includeContentType = true) => {
  const headers = {};
  
  if (includeContentType) {
    headers['Content-Type'] = 'application/json';
  }
  
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  
  return headers;
};

/**
 * Get headers for form-data requests (multipart/form-data)
 */
export const getFormDataHeaders = () => {
  const headers = {};
  const token = getAuthToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  // Don't set Content-Type for FormData - browser will set it with boundary
  return headers;
};

export default API_CONFIG;

